import { useEffect } from "react";

import { OutcomeChipList } from "./OutcomeChipList";

import { OUTCOME_ID_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { OutcomesNavSidebar } from "components/materials/outcomes/OutcomesNavSidebar";
import { FakeStudentOtherSectionOutcomeUseIndex } from "components/outcomes/FakeStudentOtherSectionOutcomeUseIndex";
import { OutcomeInfo } from "components/outcomes/OutcomeInfo";
import { OutcomeUseIndex } from "components/outcomes/OutcomeUseIndex";
import { StudentOtherSectionOutcomeUseIndex } from "components/outcomes/StudentOtherSectionOutcomeUseIndex";
import { StudentOutcomeUseIndex } from "components/outcomes/StudentOutcomeUseIndex";
import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectFullSectionOutcomes,
  selectSectionOutcomeRubric,
} from "store/selectors";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { Link } from "utils/routing";
import { sectionHomeUrl, sectionOutcomesUrl, withQueryParams } from "utils/urls";
import { isStudentDemoEnabled } from "utils/waffle-flags";

export const OutcomesView = () => {
  const isSmallScreen = !useIsMdOrLarger();
  const { canAuthorSection, canParticipateInSection } = useCapabilities();

  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionOutcomes = useAppSelector(selectFullSectionOutcomes);
  const outcomes = sectionOutcomes.map((sectionOutcome) => sectionOutcome.outcome);

  const currentOutcome = useFindItemBySearchParamId(outcomes, OUTCOME_ID_QUERY_PARAM);
  const outcomeId = currentOutcome?.id;

  const currentSectionOutcome = sectionOutcomes.find(
    (sectionOutcome) => sectionOutcome.outcome_id === outcomeId,
  );

  const rubric = useAppSelector((s) => selectSectionOutcomeRubric(s, currentSectionOutcome?.id));

  useEffect(() => {
    if (!sectionId) {
      return;
    }
    storeApi.outcomes.list();
    storeApi.section_outcomes.list({ section_id: sectionId });

    storeApi.orgs.list();
  }, [sectionId]);

  if (!sectionId) {
    return null;
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto">
      {isSmallScreen && outcomes.length > 0 && (
        <div className="mt-4 w-full">
          <OutcomeChipList
            getOutcomeUrl={(outcome) => sectionOutcomesUrl(sectionId, outcome.id)}
            outcomes={outcomes}
            selectedOutcomeId={outcomeId}
          />

          {currentSectionOutcome && (
            <OutcomeInfo
              outcomeItem={currentSectionOutcome}
              rubric={rubric}
              isSectionLevel
              readOnly
            />
          )}

          {canAuthorSection && (
            <div className="space-y-2">
              <SquareButton
                as={Link}
                className="w-full max-w-full grow md:w-auto md:max-w-fit md:grow-0"
                kind="tertiary"
                size="m"
                to={withQueryParams(sectionHomeUrl(sectionId), {
                  [TAB_QUERY_PARAM]: "assessments",
                })}
              >
                {t("section.view_assessments")}
              </SquareButton>

              <SquareButton
                as={Link}
                className="w-full max-w-full grow md:w-auto md:max-w-fit md:grow-0"
                kind="tertiary"
                size="m"
                to={withQueryParams(sectionHomeUrl(sectionId), {
                  [TAB_QUERY_PARAM]: "engagement",
                })}
              >
                {t("section.view_engagement")}
              </SquareButton>
            </div>
          )}
        </div>
      )}

      {!isSmallScreen && (
        <div className="flex">
          <OutcomesNavSidebar sectionId={sectionId} selectedOutcome={currentOutcome?.title} />

          <main className="h-full w-full">
            <OutcomeInfo
              outcomeItem={currentSectionOutcome}
              rubric={rubric}
              isSectionLevel
              // For now we only allow editing the description and title for outcomes at the org level page, even though
              // the instructor can edit the rubric at the course level.
              readOnly
            />
          </main>

          {outcomeId && !currentOutcome && <div className="page">{t("error.404_message")}</div>}
        </div>
      )}

      {currentSectionOutcome && canAuthorSection && (
        <OutcomeUseIndex sectionOutcome={currentSectionOutcome} />
      )}

      {currentSectionOutcome && canParticipateInSection && (
        <StudentOutcomeUseIndex sectionOutcome={currentSectionOutcome} />
      )}

      {currentSectionOutcome && canParticipateInSection && !isStudentDemoEnabled() && (
        <StudentOtherSectionOutcomeUseIndex sectionOutcome={currentSectionOutcome} />
      )}

      {currentSectionOutcome && canParticipateInSection && isStudentDemoEnabled() && (
        <FakeStudentOtherSectionOutcomeUseIndex sectionOutcome={currentSectionOutcome} />
      )}
    </div>
  );
};
